import { Mui, MuiIcons, MuiStyles, MuiX, DateFns } from "@osu/react-ui";
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { setRowSelection, setDisplayComponent, setOpenDialog, setActionType, setCompletionDate, selectManageContent, clearRowData } from '../../services/graphql/slices/manage-applications-slice';
import { useStore } from "react-redux";
import { useSelector, useDispatch } from 'react-redux';
import { useListAssignableApplicationsQuery, useListAssignedApplicationsQuery, useBulkAssignUnassignApplicationsMutation, graphqlApi } from '../../services/graphql/api';
import useTableStyles from '../../Common/tableStyles';
import AssignmentAlerts from "./assignmentAlerts";
import { addBusinessDays } from "../../util/functions";
import ServerSideGrid from "./ServerSideGrid";
import ClientSideGrid from "./ClientSideGrid";

const useStyles = () => ({
  mainContent: {
    padding: "0.75rem",
  },
});

export default function AssignableOrAssignedApplications({ selectedTab }) {
  const store = useStore();
  const theme = MuiStyles.useTheme();
  const dispatch = useDispatch();
  const resultData = useSelector(selectManageContent)?.result;
  const { sharedTableStyles } = useTableStyles();
  const classes = useStyles(theme);

  const { displayComponent } = useSelector((state) => state.manageContentSlice);
  const isAssignablePage = displayComponent === "assignable";
  const title = isAssignablePage ? "Eligible" : "Assigned";
  const [selectedDate, setSelectedDate] = useState(() => addBusinessDays(new Date(), 5));
  const [paginationModel, setPaginationModel] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [pageSizeInput, setPageSizeInput] = useState(100);

  useEffect(() => {
    setPageSizeInput(paginationModel.pageSize);
  }, [paginationModel.pageSize]);

  //
  // Row data & applicant selected (checkboxed) data management
  //
  const reviewerEmployeeId = selectedTab?.reviewerEmployeeId;
  const programId = selectedTab?.programId || "honors";

  const assignableQuery = useListAssignableApplicationsQuery({
    programId,
    reviewerEmployeeId,
    sortModel: sortModel.map(sort => ({
      ...sort,
      id: sort.id === "id" ? "applicantEmployeeId" : sort.id
    })),
    filterModel: filterModel.map(filter => ({
      id: filter.id === "id" ? "applicantEmployeeId" : filter.id,
      value: JSON.stringify(filter.value),
      operator: filter.operator
    })),
    paginationModel,
  }, {
    refetchOnMountOrArgChange: true,
    skip: !reviewerEmployeeId || !isAssignablePage
  });

  const assignedQuery = useListAssignedApplicationsQuery({
    programId,
    reviewerEmployeeId,
    sortModel: sortModel.map(sort => ({
      ...sort,
      id: sort.id === "id" ? "applicantEmployeeId" : sort.id
    })),
    filterModel: filterModel.map(filter => ({
      id: filter.id === "id" ? "applicantEmployeeId" : filter.id,
      value: filter.value,
      operator: filter.operator
    })),
    paginationModel,
  }, {
    refetchOnMountOrArgChange: true,
    skip: !reviewerEmployeeId || isAssignablePage
  });

  const { isFetching, refetch } = isAssignablePage ? assignableQuery : assignedQuery;
  const assignedData = assignedQuery.data || [];
  const clear = useCallback(() => {
        setPaginationModel({
            pageIndex: 0,
            pageSize: 100,
        });
        setSortModel([]);
        setFilterModel([]);
        setSelectedRows({});
        dispatch(clearRowData());
    }, [dispatch]);
    
  // Reset only if a reviewer is currently selected
  useEffect(() => { if (reviewerEmployeeId) { clear(); } }, [reviewerEmployeeId, clear]);

  useEffect(() => {
    // Pagination changes - selections are preserved across pages
  }, [paginationModel.pageIndex, paginationModel.pageSize, dispatch]); // eslint-disable-line

  const [_ignore, assignUnassignResult] = useBulkAssignUnassignApplicationsMutation({ fixedCacheKey: "manage-applications-dialog" }); // eslint-disable-line

  useEffect(() => {
    if (assignUnassignResult?.isLoading === false) { 
      if (assignUnassignResult?.isSuccess) {
        setSelectedRows({});
        dispatch(setRowSelection({}));
      }
      return; 
    }
    store.dispatch(graphqlApi.util.invalidateTags([`list-assignable`])); 
    store.dispatch(graphqlApi.util.invalidateTags([`list-assigned`]));
  }, [assignUnassignResult?.isLoading, assignUnassignResult?.isSuccess, dispatch, store]);


  function toggleViewFn(type) {
    store.dispatch(graphqlApi.util.invalidateTags([`list-${type}`]));
    dispatch(setDisplayComponent(type));
    dispatch(setRowSelection({}));
    clear();
  }

  const getApplications = (apps) => {
    if (!Array.isArray(apps)) return [];
    return apps.map((app) => ({
      id: app.applicantEmployeeId,
      applicantEmployeeId: app.applicantEmployeeId,
      firstName: app.firstName,
      lastName: app.lastName,
      reviewType: app.reviewType,
      review: app.review,
      programId: app.programId,
      term: app.term,
      campusDescription: app.campusDescription,
      applicationDate: app.applicationDate ? new Date(app.applicationDate).toLocaleDateString() : "",
      ...app,
    }));
  };

  // Build rows from query data
  const rows = useMemo(() => {
    let apps;
    if(isAssignablePage) {
      apps = resultData?.reviewer?.assignableApplications;
    }
    else {
      apps = resultData?.reviewer?.assignedApplications;
    }
    return getApplications(apps);
  }, [resultData, isAssignablePage]);

  const nestedFieldColumns = useMemo(() => {
    if (!resultData?.columns || resultData.columns.length === 0) return {};
    
    const nestedCols = resultData.columns.filter(col => col.field && col.field.includes('.'));
    const grouped = {};
    
    nestedCols.forEach(col => {
      const parentField = col.field.split('.')[0];
      if (!grouped[parentField]) {
        grouped[parentField] = [];
      }
      grouped[parentField].push(col);
    });
    
    return grouped;
  }, [resultData?.columns]);

  const handleRowSelectionChange = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(selectedRows) : updater;
    
    const currentEnrichedSelection = store.getState().manageContentSlice?.rowSelection || {};
    const enrichedSelection = { ...currentEnrichedSelection };
    
    Object.keys(enrichedSelection).forEach(applicantEmployeeId => {
      if (!next || !next[applicantEmployeeId]) {
        delete enrichedSelection[applicantEmployeeId];
      }
    });
    
    Object.keys(next || {}).forEach(applicantEmployeeId => {
      if (next[applicantEmployeeId] && !enrichedSelection[applicantEmployeeId]) {
        const rowData = rows.find(row => row.applicantEmployeeId === applicantEmployeeId);
        if (rowData) {
          enrichedSelection[applicantEmployeeId] = {
            applicantEmployeeId: rowData.applicantEmployeeId,
            programId: rowData.programId,
            term: rowData.term,
          };
        }
      }
    });
    
    setSelectedRows(next || {});
    dispatch(setRowSelection(enrichedSelection));
  }, [selectedRows, store, rows, dispatch]);

  //
  // Column Visibility & Column Order Handling
  //

  // localStorage persistence for column settings
  const storageKey = `manage-applications-${programId}-${displayComponent}`;

  const hasHydratedFiltersRef = useRef(false);
  const previousFilterModelRef = useRef([]);
  const hasHydratedColumnsRef = useRef(false);

  useEffect(() => {
    hasHydratedFiltersRef.current = false;
    previousFilterModelRef.current = [];

    const saved = localStorage.getItem(storageKey);
    if (!saved) {
      hasHydratedFiltersRef.current = true;
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed?.columnFilters)) {
        setFilterModel(parsed.columnFilters);
        previousFilterModelRef.current = parsed.columnFilters;
      }
    } catch (err) {
      console.warn('Failed to parse saved filters');
    } finally {
      hasHydratedFiltersRef.current = true;
    }
  }, [storageKey]);

  useEffect(() => {
    if (!hasHydratedFiltersRef.current) return;

    const changed = JSON.stringify(filterModel) !== JSON.stringify(previousFilterModelRef.current);
    previousFilterModelRef.current = filterModel;

    if (!changed) return;

    const saved = localStorage.getItem(storageKey);
    let existing = {};

    if (saved) {
      try {
        existing = JSON.parse(saved);
      } catch (err) {
        console.warn('Failed to parse saved filters');
      }
    }

    localStorage.setItem(storageKey, JSON.stringify({ ...existing, columnFilters: filterModel }));
  }, [filterModel, storageKey]);

  const previousColumnIdsRef = useRef([]);
  const previousColumnOrderRef = useRef([]);
  const initialColumnVisibilityRef = useRef({});
  const defaultColumnVisibilityRef = useRef({});
  const skipPersistenceRef = useRef(false);

  useEffect(() => {
    previousColumnIdsRef.current = [];
    previousColumnOrderRef.current = [];
    hasHydratedColumnsRef.current = false;
  }, [storageKey]);

  // Force re-hydration when switching reviewers under the same storage key
  useEffect(() => {
    previousColumnIdsRef.current = [];
    previousColumnOrderRef.current = [];
    hasHydratedColumnsRef.current = false;
  }, [reviewerEmployeeId]);

  function getReIdedField(fieldId) { return { "applicantEmployeeId": "id" }[fieldId] ?? fieldId; } // TODO Get rid of this.. should just be using applicantEmployeeId consistently
  const mapLegacyId = useCallback((id) => id === "applicantEmployeeId" ? "id" : id, []);

  const baseColumnIds = useMemo(() => {
    return (resultData?.columns || [])
      .filter(col => !col.field.includes('.'))
      .map(col => getReIdedField(col.field));
  }, [resultData?.columns]);

  const defaultColumnOrder = useMemo(() => {
    if (!baseColumnIds.length) return [];
    return ["mrt-row-expand", "mrt-row-select", ...baseColumnIds];
  }, [baseColumnIds]);

  const defaultColumnVisibility = useMemo(() => {
    const visibility = {};
    (resultData?.columns || [])
      .filter(col => !col.field.includes('.'))
      .forEach(col => {
        const id = getReIdedField(col.field);
        visibility[id] = col.default === true;
      });
    return visibility;
  }, [resultData?.columns]);

  const initialColumnVisibility = useMemo(() => {
    const saved = localStorage.getItem(storageKey);
    let savedSettings = null;
    if (saved) {
      try {
        savedSettings = JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved column visibility');
      }
    }

    const visibility = { ...defaultColumnVisibility };
    if (savedSettings?.columnVisibility) {
      Object.entries(savedSettings.columnVisibility).forEach(([id, val]) => {
        visibility[id] = val;
      });
    }
    return visibility;
  }, [defaultColumnVisibility, storageKey]);

  const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);
  const [columnOrder, setColumnOrder] = useState(defaultColumnOrder);

  useEffect(() => {
    if (!baseColumnIds.length) return;

    const currentColumnIds = [...baseColumnIds].sort();
    const previousColumnIdsKey = previousColumnIdsRef.current.join(',');
    const currentColumnIdsKey = currentColumnIds.join(',');
    const columnsChanged = currentColumnIdsKey !== previousColumnIdsKey;

    const shouldHydrate = columnsChanged || !hasHydratedColumnsRef.current;

    if (shouldHydrate) {
      const saved = localStorage.getItem(storageKey);
      let savedOrder = [];
      if (saved) {
        try {
          const savedSettings = JSON.parse(saved);
          if (Array.isArray(savedSettings?.columnOrder)) {
            savedOrder = savedSettings.columnOrder.filter(Boolean).map(mapLegacyId);
          }
        } catch (e) {
          console.warn('Failed to parse saved column order');
        }
      }

      const currentIdSet = new Set(defaultColumnOrder);
      const filteredSaved = savedOrder.filter((id) => currentIdSet.has(id));
      const missing = defaultColumnOrder.filter((id) => !filteredSaved.includes(id));
      const nextOrder = filteredSaved.length > 0 ? [...filteredSaved, ...missing] : defaultColumnOrder;

      setColumnVisibility(initialColumnVisibility);
      setColumnOrder(nextOrder);
      initialColumnVisibilityRef.current = initialColumnVisibility;
      defaultColumnVisibilityRef.current = defaultColumnVisibility;
      previousColumnOrderRef.current = nextOrder;
      previousColumnIdsRef.current = currentColumnIds;
    }

    hasHydratedColumnsRef.current = true;
  }, [baseColumnIds, columnOrder, defaultColumnOrder, initialColumnVisibility, storageKey]);

  useEffect(() => {
    initialColumnVisibilityRef.current = initialColumnVisibility;
    defaultColumnVisibilityRef.current = defaultColumnVisibility;
  }, [initialColumnVisibility, defaultColumnVisibility]);

  const handleColumnVisibilityChange = useCallback((nextVisibilityOrUpdater) => {
    setColumnVisibility((prev) => {
      const resolved = typeof nextVisibilityOrUpdater === 'function' ? nextVisibilityOrUpdater(prev) : nextVisibilityOrUpdater;
      const changed = JSON.stringify(resolved) !== JSON.stringify(prev);

      if (hasHydratedColumnsRef.current && changed) {
        if (skipPersistenceRef.current) { return resolved; }
        const saved = localStorage.getItem(storageKey);
        let existing = {};

        if (saved) {
          try {
            existing = JSON.parse(saved);
          } catch (err) {
            console.warn('Failed to parse saved column visibility');
          }
        }

        localStorage.setItem(storageKey, JSON.stringify({ ...existing, columnVisibility: resolved }));
      }

      return resolved;
    });
  }, [storageKey]);

  const handleColumnOrderChange = useCallback((newColumnOrder) => {
    const resolved = typeof newColumnOrder === 'function' ? newColumnOrder(columnOrder) : newColumnOrder;
    const filtered = (resolved || []).filter(Boolean).map(mapLegacyId);

    const allowed = new Set(defaultColumnOrder);
    const filteredValid = filtered.filter((id) => allowed.has(id));
    const matchesDefaultOrder = JSON.stringify(filteredValid) === JSON.stringify(defaultColumnOrder);

    const currentColumnIds = [...baseColumnIds].sort().join(',');
    const previousColumnIdsKey = previousColumnIdsRef.current.join(',');
    const columnSetUnchanged = currentColumnIds === previousColumnIdsKey;
    const orderChanged = JSON.stringify(filteredValid) !== JSON.stringify(previousColumnOrderRef.current);

    previousColumnOrderRef.current = filteredValid;
    previousColumnIdsRef.current = [...baseColumnIds].sort();

    if (skipPersistenceRef.current) {
      setColumnOrder(filteredValid.length ? filteredValid : defaultColumnOrder);
      return;
    }

    if (hasHydratedColumnsRef.current && columnSetUnchanged && orderChanged) {
      const saved = localStorage.getItem(storageKey);
      let existing = {};

      if (saved) {
        try {
          existing = JSON.parse(saved);
        } catch (err) {
          console.warn('Failed to parse saved column order');
        }
      }

      if (matchesDefaultOrder) {
        if (existing.columnOrder) {
          delete existing.columnOrder;
          if (Object.keys(existing).length === 0) {
            localStorage.removeItem(storageKey);
          } else {
            localStorage.setItem(storageKey, JSON.stringify(existing));
          }
        }
      } else {
        localStorage.setItem(storageKey, JSON.stringify({ ...existing, columnOrder: filteredValid }));
      }
    }

    setColumnOrder(filteredValid.length ? filteredValid : defaultColumnOrder);
  }, [baseColumnIds, columnOrder, defaultColumnOrder, mapLegacyId, storageKey]);

  function resetColumns() {
    skipPersistenceRef.current = true;
    localStorage.removeItem(storageKey);
    const resetVisibility = defaultColumnVisibilityRef.current || defaultColumnVisibility || {};
    setColumnVisibility(resetVisibility);
    setColumnOrder(defaultColumnOrder);
    initialColumnVisibilityRef.current = initialColumnVisibility;
    defaultColumnVisibilityRef.current = defaultColumnVisibility;
    previousColumnOrderRef.current = defaultColumnOrder;
    previousColumnIdsRef.current = [...baseColumnIds].sort();

    // Release skip flag on next tick so user actions persist again
    setTimeout(() => { skipPersistenceRef.current = false; }, 0);
  }
  
  //
  // Assign/unassign dialog
  //
  function openDialogFn(type) {
    dispatch(setActionType(type));
    dispatch(setOpenDialog(true));
  }
  
  const filtersActiveStyle = { color: "#C04F24", marginLeft: "0.5rem", fontSize: "1rem" };
  const hasActiveFilter = filterModel?.length > 0;

  const renderTopToolbarContent = useCallback(() => (
    <Mui.Box sx={classes.mainContent}>
      <Mui.Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <DateFns.LocalizationProvider>
          <MuiX.DatePicker
            disablePast={true}
            label="Complete By"
            value={selectedDate}
            onChange={(newValue) => {
              setSelectedDate(newValue);
              if (isNaN(newValue.getTime())) { return; }
              dispatch(setCompletionDate(newValue.toISOString()));
            }}
            sx={{ marginRight: '1rem' }}
          />
        </DateFns.LocalizationProvider>
        <AssignButtons
          showButton={isAssignablePage}
          disabled={Object.keys(selectedRows).length === 0 || isNaN(selectedDate.getTime())}
          openDialogFn={openDialogFn}
          toggleViewFn={toggleViewFn}
          isFetching={isFetching}
          assignedData={assignedData}
        />
        <UnassignButtons
          showButton={!isAssignablePage}
          disabled={Object.keys(selectedRows).length === 0}
          openDialogFn={openDialogFn}
          toggleViewFn={toggleViewFn}
        />
        <Mui.Button
          color="secondary"
          disabled={!hasActiveFilter}
          startIcon={<MuiIcons.Clear />}
          onClick={() => setFilterModel([])}
          sx={{ ml: 2, display: isAssignablePage ? "default" : "none" }} // Hide button for client-side grid
        >
          Clear Filters {hasActiveFilter && <MuiIcons.FiberManualRecord sx={filtersActiveStyle} />}
        </Mui.Button>
      </Mui.Box>
      <AssignmentAlerts />
      <Mui.Typography sx={{ marginTop: '1rem' }}>{title} Applicants</Mui.Typography>
    </Mui.Box>
  ), [classes.mainContent, selectedDate, dispatch, isAssignablePage, selectedRows, isFetching, assignedData, title, hasActiveFilter]);
  
  if (!selectedTab) { return <></>; }

  return (
    <Mui.Box>
      <Mui.LinearProgress sx={{ visibility: (isFetching ? undefined : "hidden" ) }} />
      {isAssignablePage ? (
        <ServerSideGrid
          rows={rows}
          columns={[]}
          resultData={resultData}
          isFetching={isFetching}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onRowSelectionChange={handleRowSelectionChange}
          columnVisibility={columnVisibility}
          setColumnVisibility={handleColumnVisibilityChange}
          columnOrder={columnOrder}
          setColumnOrder={handleColumnOrderChange}
          sharedTableStyles={sharedTableStyles}
          theme={theme}
          renderTopToolbarContent={renderTopToolbarContent}
          pageSizeInput={pageSizeInput}
          setPageSizeInput={setPageSizeInput}
          nestedFieldColumns={nestedFieldColumns}
          resetColumns={resetColumns}
          onRefresh={refetch}
          canRefresh={!isFetching}
        />
      ) : (
        <ClientSideGrid
          rows={rows}
          resultData={resultData}
          isFetching={isFetching}
          selectedRows={selectedRows}
          onRowSelectionChange={handleRowSelectionChange}
          columnVisibility={columnVisibility}
          setColumnVisibility={handleColumnVisibilityChange}
          columnOrder={columnOrder}
          setColumnOrder={handleColumnOrderChange}
          sharedTableStyles={sharedTableStyles}
          theme={theme}
          renderTopToolbarContent={renderTopToolbarContent}
          pageSizeInput={pageSizeInput}
          setPageSizeInput={setPageSizeInput}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          nestedFieldColumns={nestedFieldColumns}
          resetColumns={resetColumns}
          onRefresh={refetch}
          canRefresh={!isFetching}
        />
      )}
    </Mui.Box>
  );
}

function AssignButtons({ showButton, disabled, openDialogFn, toggleViewFn, isFetching, assignedData }) {
  const [_ignore, result] = useBulkAssignUnassignApplicationsMutation({ fixedCacheKey: "manage-applications-dialog" }); // eslint-disable-line
  if (!showButton) { return; }

  function openDialog() { openDialogFn("assign"); }
  function showAssigned() { toggleViewFn("assigned"); }

  const label = result?.isLoading ? "Assigning..." : "Assign";
  const spinner = result?.isLoading ? <Mui.CircularProgress size={20} style={{ color: 'gray', marginRight: '1rem' }} /> : <></>;


  return (
    <>
      <Mui.Button disabled={result?.isLoading || disabled} onClick={openDialog} variant="outlined">{spinner} {label}</Mui.Button>
      <Mui.Button onClick={showAssigned} variant="outlined">Show Assigned</Mui.Button>
    </>
  );
}

function UnassignButtons({ showButton, disabled, openDialogFn, toggleViewFn }) {
  const [_ignore, result] = useBulkAssignUnassignApplicationsMutation({ fixedCacheKey: "manage-applications-dialog" }); // eslint-disable-line
  if (!showButton) { return; }

  function openDialog() { openDialogFn("unassign"); }
  function showUnassigned() { toggleViewFn("assignable"); }

  const label = result?.isLoading ? "Unassigning..." : "Unassign";
  const spinner = result?.isLoading ? <Mui.CircularProgress size={20} style={{ color: 'gray', marginRight: '1rem' }} /> : <></>;

  return (
    <>
      <Mui.Button disabled={result?.isLoading || disabled} onClick={openDialog} variant="outlined">{spinner} {label}</Mui.Button>
      <Mui.Button onClick={showUnassigned} variant="outlined">Show Unassigned</Mui.Button>
    </>
  );
}